import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Services" },
  { to: "/entreprises", label: "Entreprises" },
  { to: "/communaute", label: "Communauté" },
  { to: "/a-propos", label: "À propos" },
  { to: "/ressources", label: "Ressources" },
  { to: "/tarifs", label: "Tarifs" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  const renderLink = (l) => (
    <NavLink
      key={l.to}
      to={l.to}
      data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|à/g, "-")}`}
      className={({ isActive }) =>
        `group relative text-xs uppercase tracking-[0.16em] transition-colors duration-300 ${
          isActive ? "text-olive" : "text-olive/70 hover:text-olive"
        }`
      }
    >
      {({ isActive }) => (
        <span className="relative whitespace-nowrap py-1">
          {l.label}
          <span
            className={`absolute -bottom-0.5 left-1/2 h-px -translate-x-1/2 bg-gold transition-all duration-500 ${
              isActive ? "w-full" : "w-0 group-hover:w-full"
            }`}
          />
        </span>
      )}
    </NavLink>
  );

  return (
    <>
    <header
      data-testid="main-navbar"
      className="fixed inset-x-0 top-0 z-50 border-b border-deep/10 shadow-[0_10px_40px_rgba(30,42,74,0.08)]"
    >
      {/* Arrière-plan lumineux : crème éclatant, rayons de soleil dorés et halo depuis le coin supérieur gauche */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(100deg, #FFFDF6 0%, #FBF7EC 45%, #F5EEDA 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "conic-gradient(from 100deg at 0% 0%, transparent 0deg, rgba(197,160,89,0.22) 7deg, transparent 13deg, rgba(197,160,89,0.12) 21deg, transparent 27deg, rgba(197,160,89,0.18) 36deg, transparent 42deg, rgba(197,160,89,0.10) 53deg, transparent 60deg, rgba(197,160,89,0.15) 72deg, transparent 80deg, transparent 90deg)",
            filter: "blur(2px)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(640px 240px at 0% 0%, rgba(255,224,160,0.55), rgba(255,224,160,0) 70%)",
          }}
        />
      </div>

      <div className="relative mx-auto flex max-w-[1500px] items-center justify-between gap-8 px-6 py-4 md:px-12 lg:px-16">
        <nav className="hidden flex-1 items-center justify-between gap-6 lg:flex" data-testid="nav-links-desktop">
          {LINKS.map(renderLink)}
        </nav>

        <div className="flex items-center justify-end gap-8">
          <Link
            to="/rendez-vous"
            data-testid="nav-cta-button"
            className="group hidden items-center gap-2 whitespace-nowrap rounded-full bg-deep px-6 py-3 text-xs uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-gold hover:text-deep lg:inline-flex"
          >
            Prendre rendez-vous
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="text-deep lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-links-mobile"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 flex flex-col lg:hidden"
            style={{
              background:
                "radial-gradient(500px 320px at 0% 0%, rgba(255,224,160,0.45), rgba(255,224,160,0) 70%), linear-gradient(160deg, #FFFDF6 0%, #FBF7EC 50%, #F5EEDA 100%)",
            }}
          >
            <nav className="flex flex-1 flex-col justify-center px-8 pt-24">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-link-mobile-${l.label.toLowerCase().replace(/\s|à/g, "-")}`}
                    className={({ isActive }) =>
                      `group flex items-baseline gap-4 border-b border-deep/5 py-3.5 ${
                        isActive ? "text-gold" : "text-olive"
                      }`
                    }
                  >
                    <span className="font-serif text-sm italic text-gold/70">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-3xl font-light tracking-tight transition-colors duration-300 group-hover:text-gold">
                      {l.label}
                    </span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              className="px-8 pb-10"
            >
              <Link
                to="/rendez-vous"
                onClick={() => setOpen(false)}
                data-testid="nav-cta-button-mobile"
                className="group flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 text-xs uppercase tracking-[0.2em] text-deep transition-colors duration-300 hover:bg-deep hover:text-cream"
              >
                Prendre rendez-vous
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <p className="mt-6 text-center text-xs tracking-[0.15em] text-deep/40">
                info@emergence-prudhomme.com&nbsp;&nbsp;•&nbsp;&nbsp;+1 514 621-4802
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
